import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { selectSessionAdvisor, fetchSessionForAuthRequest, selectFeatureFlags } from '@client/model';
import { advisorsApi } from '@client/api/waterlily';
import {
  EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
  EDWARD_JONES_ORGANIZATION_NAME,
} from '@shared';

export const advisorBasicInfoSchema = z.object({
  advisorFirstName: z.string().min(1, 'First name is required'),
  advisorLastName: z.string().min(1, 'Last name is required'),
  organizationDisplayName: z.string().min(1, 'Organization name is required'),
  organizationName: z.string().optional(),
});

export type AdvisorBasicInfoFormValues = z.infer<typeof advisorBasicInfoSchema>;

interface UseAdvisorOnboardingBasicInfoFormOptions {
  onSaveSuccess?: () => void;
}

export function useAdvisorOnboardingBasicInfoForm(
  { onSaveSuccess }: UseAdvisorOnboardingBasicInfoFormOptions = {},
) {
  const dispatch = useDispatch();

  const advisor = useSelector(selectSessionAdvisor);
  const featureFlags = useSelector(selectFeatureFlags);
  const isEdjPilotAdvisor = !!featureFlags.edjPilotAdvisor;

  const form = useForm<AdvisorBasicInfoFormValues>({
    resolver: zodResolver(advisorBasicInfoSchema),
    mode: 'onChange',
    defaultValues: {
      advisorFirstName: '',
      advisorLastName: '',
      organizationDisplayName: '',
      organizationName: '',
    },
  });

  const updateAdvisorMutation = useMutation({
    mutationFn: (data: AdvisorProfile) => advisorsApi.updateById(advisor.advisorId!, data),
    onSuccess: () => {
      toast.success('Profile Saved', {
        description: 'Your information has been saved.',
      });
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
      onSaveSuccess?.();
    },
    onError: () => {
      toast.error('Failed to save profile', {
        description: 'An unexpected error occurred. Please try again.',
      });
    },
  });

  // Check if the advisor profile has all required fields filled (from the persisted advisor data)
  const isProfileComplete = useMemo(() => {
    return Boolean(
      advisor.advisorFirstName?.trim() &&
        advisor.advisorLastName?.trim() &&
        advisor.organizationDisplayName?.trim(),
    );
  }, [advisor.advisorFirstName, advisor.advisorLastName, advisor.organizationDisplayName]);

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    if (advisor.advisorId && (!isFormInitialized || !form.formState.isDirty)) {
      form.reset({
        advisorFirstName: advisor.advisorFirstName ?? '',
        advisorLastName: advisor.advisorLastName ?? '',
        organizationDisplayName: isEdjPilotAdvisor
          ? EDWARD_JONES_ORGANIZATION_DISPLAY_NAME
          : (advisor.organizationDisplayName ?? ''),
        organizationName: isEdjPilotAdvisor
          ? EDWARD_JONES_ORGANIZATION_NAME
          : (advisor.organizationName ?? ''),
      });
      setIsFormInitialized(true);
    }
  }, [
    advisor.advisorId,
    advisor.advisorFirstName,
    advisor.advisorLastName,
    advisor.organizationDisplayName,
    advisor.organizationName,
    isEdjPilotAdvisor,
    isFormInitialized,
    form,
  ]);

  const onSubmit = (data: AdvisorBasicInfoFormValues) => {
    if (!advisor.advisorId) {
      toast.error('Unable to proceed', {
        description: 'Advisor ID is missing. Please refresh and try again.',
      });
      return;
    }

    if (!form.formState.isDirty && isProfileComplete) {
      onSaveSuccess?.();
      return;
    }

    const displayName = isEdjPilotAdvisor
      ? EDWARD_JONES_ORGANIZATION_DISPLAY_NAME
      : data.organizationDisplayName.trim();
    const legalName = isEdjPilotAdvisor
      ? EDWARD_JONES_ORGANIZATION_NAME
      : (data.organizationName?.trim() || displayName);

    updateAdvisorMutation.mutate(
      {
        ...advisor,
        advisorFirstName: data.advisorFirstName.trim(),
        advisorLastName: data.advisorLastName.trim(),
        organizationName: legalName,
        organizationDisplayName: displayName,
      },
      {
        onSuccess: () => {
          // Reset the form with the saved values to clear the dirty state
          form.reset({
            advisorFirstName: data.advisorFirstName.trim(),
            advisorLastName: data.advisorLastName.trim(),
            organizationDisplayName: displayName,
            organizationName: legalName,
          });
        },
      },
    );
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: updateAdvisorMutation.isPending,
    isValid: form.formState.isValid,
    isProfileComplete,
    isFormInitialized,
  };
}
